// A d3-format SUBSET, for the format strings the `superset/` corpus authors
// (`y_axis_format` on a big_number, `d3_number_format` on a table column,
// `value_format` on a funnel).
//
// Not the real `d3-format` package: almost every corpus string is one of the
// four shapes below, and the full grammar's locale machinery, SI prefixes,
// exponents and padding are unused.
//
// THE RULE: an unrecognized format returns null and the caller falls back to
// its own default rendering. It never guesses. A half-applied format (dropping
// the `%` but keeping the precision) renders 0.42 as "0.4" under a rate label:
// "trusted label, wrong number".

/** The shapes this module understands, matched in order:
 *
 *  - `.2%`  / `%`      a percentage: multiply by 100, append `%`
 *  - `$,.0f` / `$.2f`  currency: a `$` prefix, optional thousands grouping
 *  - `,.1f` / `.3f`    a fixed-point decimal, optional thousands grouping
 *  - `,d` / `d`        an integer, optional thousands grouping
 *
 *  The optional `,` is d3's thousands-grouping flag and the optional `.N` its
 *  precision, exactly as in d3-format. Anything else falls out. */
const FORMAT_RE = /^(\$)?(,)?(?:\.(\d+))?([%fd])$/;

export interface ParsedNumberFormat {
  currency: boolean;
  grouped: boolean;
  /** Digits after the decimal point. Defaults per type, matching d3: 0 for
   *  `d`, 2 for `f`, 0 for `%`. */
  precision: number;
  kind: "percent" | "fixed" | "integer";
}

/** Parses a d3 format string into the pieces `formatNumber` applies, or `null`
 *  when it is outside the supported subset. Exported so a caller can ask "is
 *  this format one I can honor?" once, up front, rather than per value. */
export function parseNumberFormat(format: string | null | undefined): ParsedNumberFormat | null {
  if (!format) return null;
  const match = FORMAT_RE.exec(format.trim());
  if (!match) return null;
  const [, currency, grouped, precision, type] = match;
  const kind = type === "%" ? "percent" : type === "f" ? "fixed" : "integer";
  const defaultPrecision = kind === "fixed" ? 2 : 0;
  return {
    currency: currency === "$",
    grouped: grouped === ",",
    precision: precision === undefined ? defaultPrecision : Number(precision),
    kind,
  };
}

/** Applies a parsed format to one value. Non-finite input renders as "-", the
 *  same empty-cell mark the console's tables use, rather than "NaN". */
export function applyNumberFormat(value: number, format: ParsedNumberFormat): string {
  if (!Number.isFinite(value)) return "-";
  const scaled = format.kind === "percent" ? value * 100 : value;
  const body = scaled.toLocaleString("en-US", {
    minimumFractionDigits: format.precision,
    maximumFractionDigits: format.precision,
    useGrouping: format.grouped,
  });
  return `${format.currency ? "$" : ""}${body}${format.kind === "percent" ? "%" : ""}`;
}

/** The one-shot form: format `value` per `format`, or `null` if the format is
 *  outside the subset (so the caller keeps its own default rendering). */
export function formatNumber(value: number, format: string | null | undefined): string | null {
  const parsed = parseNumberFormat(format);
  return parsed === null ? null : applyNumberFormat(value, parsed);
}

/** Thresholds for `formatCompactValue`, largest first. */
const COMPACT_STEPS: readonly { limit: number; divisor: number; suffix: string }[] = [
  { limit: 1_000_000_000, divisor: 1_000_000_000, suffix: "B" },
  { limit: 1_000_000, divisor: 1_000_000, suffix: "M" },
  { limit: 10_000, divisor: 1_000, suffix: "K" },
];

/**
 * A short, axis-safe rendering of one figure, honoring `format` for the UNIT
 * (the `$` prefix, the `%` suffix) while collapsing the magnitude to at most
 * four characters plus the unit.
 *
 * This is the funnel's formatter rather than `applyNumberFormat`, because a
 * funnel prints its numbers INSIDE the drawing: a bar label is a fixed slot a
 * few characters wide, and `$1,483,920` written there overlaps its neighbours
 * and stops being legible at exactly the moment the number gets interesting.
 * `$1.5M` fits. The hover card, which has room, uses the full
 * `applyNumberFormat` rendering instead — that split is deliberate, not an
 * inconsistency: the compact form is for the glance and the full one is for
 * the look.
 *
 * Below 10,000 the value is printed in full: `8,420` is both short and exact,
 * and rounding it to `8.4K` would throw away a digit for no space saving. A
 * percentage is never compacted (it is at most three digits by nature).
 */
export function formatCompactValue(value: number, format: string | null | undefined): string {
  const parsed = parseNumberFormat(format);
  if (!Number.isFinite(value)) return "-";
  if (parsed === null) return value.toLocaleString("en-US");
  if (parsed.kind === "percent") return applyNumberFormat(value, parsed);
  const magnitude = Math.abs(value);
  const step = COMPACT_STEPS.find((s) => magnitude >= s.limit);
  if (!step) {
    // Exact, but never with the authored decimals: a bar label reading
    // "$8,420.00" spends four characters saying nothing.
    return applyNumberFormat(value, { ...parsed, precision: 0, grouped: true });
  }
  const scaled = value / step.divisor;
  // One decimal below 100 of the unit ("$1.5M"), none above it ("$142M") —
  // three significant figures either way, and never more than five characters.
  const digits = Math.abs(scaled) < 100 ? 1 : 0;
  const body = scaled.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${parsed.currency ? "$" : ""}${body}${step.suffix}`;
}
